import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  subscribeUserCompanies,
  addUserCompany,
  updateUserCompany,
  deleteUserCompany,
} from '../../services/userCompaniesService';
import { Building2, Plus, Trash2, Pencil } from 'lucide-react';
import useGooglePlacesAutocomplete from '../../hooks/useGooglePlacesAutocomplete';
import { parseGooglePlaceAddress } from '../../utils/parseGooglePlaceAddress';
import { isGooglePlacesPacVisible } from '../../utils/googlePlacesPac';
import { CA_PROVINCE_CODES } from '../../constants/canadaProvinces';

/** Build one-line address for Firestore `address` (single string). */
function formatSingleLineAddressFromParsed(parsed) {
  const cityPart = [parsed.city, [parsed.province, parsed.postalCode].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
  const parts = [parsed.street, cityPart].filter(Boolean);
  let s = parts.join(', ');
  if (parsed.country && !s.includes(parsed.country)) s = s ? `${s}, ${parsed.country}` : parsed.country;
  return s;
}

function getProvincialRegLabel(t, provinceCode) {
  if (!provinceCode) return t('companies.provincialReg.default');
  const code = String(provinceCode).toUpperCase();
  const k = `companies.provincialReg.${code}`;
  const s = t(k);
  return s === k ? t('companies.provincialReg.default') : s;
}

/**
 * @param {{ embedded?: boolean }} props - When true (e.g. inside Profile modal tab), lighter chrome and no duplicate section title.
 */
export default function UserCompaniesSection({ embedded = false }) {
  const { currentUser } = useAuth();
  const { t } = useLanguage();
  const [companies, setCompanies] = useState([]);
  const [legalName, setLegalName] = useState('');
  const [address, setAddress] = useState('');
  const [addressProvince, setAddressProvince] = useState('');
  const [federalBusinessNumber, setFederalBusinessNumber] = useState('');
  const [provincialBusinessNumber, setProvincialBusinessNumber] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  /** Registration fields only after user picks an address from Google (or editing a saved company). */
  const [addressConfirmedByPlacePick, setAddressConfirmedByPlacePick] = useState(false);
  const skipNextAddressChangeRef = useRef(false);

  const companyAddressAutocompleteOptions = useMemo(
    () => ({
      componentRestrictions: { country: 'ca' },
      fields: ['address_components', 'formatted_address'],
    }),
    [],
  );

  const handleCompanyPlaceSelected = useCallback((place) => {
    skipNextAddressChangeRef.current = true;
    setAddressConfirmedByPlacePick(true);
    const parsed = parseGooglePlaceAddress(place);
    if (parsed.province) {
      const raw = String(parsed.province).toUpperCase().trim();
      const two = raw.length >= 2 ? raw.slice(0, 2) : raw;
      if (CA_PROVINCE_CODES.includes(two)) setAddressProvince(two);
      else if (CA_PROVINCE_CODES.includes(raw)) setAddressProvince(raw);
    }
    const formatted = place?.formatted_address?.trim();
    if (formatted) {
      setAddress(formatted);
    } else {
      setAddress(formatSingleLineAddressFromParsed(parsed));
    }
    queueMicrotask(() => {
      skipNextAddressChangeRef.current = false;
    });
  }, []);

  const companyAddressInputRef = useGooglePlacesAutocomplete(
    handleCompanyPlaceSelected,
    companyAddressAutocompleteOptions,
  );

  useEffect(() => {
    if (!currentUser?.uid) {
      setCompanies([]);
      return () => {};
    }
    return subscribeUserCompanies(currentUser.uid, setCompanies);
  }, [currentUser?.uid]);

  const resetForm = () => {
    setLegalName('');
    setAddress('');
    setAddressProvince('');
    setFederalBusinessNumber('');
    setProvincialBusinessNumber('');
    setEditingId(null);
    setError(null);
    setAddressConfirmedByPlacePick(false);
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setLegalName(c.legalName || '');
    setAddress(c.address || '');
    setAddressProvince((c.addressProvince || '').toUpperCase());
    setFederalBusinessNumber(c.federalBusinessNumber || '');
    setProvincialBusinessNumber(c.provincialBusinessNumber ?? c.neq ?? '');
    setError(null);
    const hasAddr = !!(c.address || '').trim();
    setAddressConfirmedByPlacePick(hasAddr);
  };

  const handleAddressChange = (e) => {
    const v = e.target.value;
    if (skipNextAddressChangeRef.current) {
      skipNextAddressChangeRef.current = false;
      setAddress(v);
      return;
    }
    setAddress(v);
    setAddressConfirmedByPlacePick(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isGooglePlacesPacVisible()) return;
    if (!currentUser?.uid) return;
    setError(null);
    setSaving(true);
    try {
      const payload = {
        legalName,
        address,
        addressProvince,
        federalBusinessNumber,
        provincialBusinessNumber,
      };
      if (editingId) {
        await updateUserCompany(currentUser.uid, editingId, payload);
      } else {
        await addUserCompany(currentUser.uid, payload);
      }
      resetForm();
    } catch (err) {
      setError(err?.message || t('companies.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (companyId) => {
    if (!currentUser?.uid || !companyId) return;
    if (!window.confirm(t('companies.deleteConfirm'))) return;
    setError(null);
    try {
      await deleteUserCompany(currentUser.uid, companyId);
      if (editingId === companyId) resetForm();
    } catch (err) {
      setError(err?.message || t('companies.deleteError'));
    }
  };

  const showRegFields = addressConfirmedByPlacePick && address.trim().length > 0;
  const provincialLabel = getProvincialRegLabel(t, addressProvince);

  return (
    <div
      className={
        embedded
          ? 'mb-0'
          : 'mb-6 border border-gray-200 rounded-lg p-4 bg-gray-50/50'
      }
    >
      {!embedded && (
        <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <Building2 className="w-4 h-4 text-indigo-600" />
          {t('companies.title')}
        </h3>
      )}
      {embedded ? (
        <p className="text-xs text-gray-600 mb-3 flex items-start gap-2">
          <Building2 className="w-4 h-4 text-indigo-600 shrink-0 mt-0.5" />
          <span>{t('companies.hint')}</span>
        </p>
      ) : (
        <p className="text-xs text-gray-600 mb-3">{t('companies.hint')}</p>
      )}

      {companies.length > 0 && (
        <ul className="space-y-2 mb-4 text-sm">
          {companies.map((c) => {
            const prov = c.provincialBusinessNumber ?? c.neq;
            return (
              <li
                key={c.id}
                className="flex items-start justify-between gap-2 py-2 border-b border-gray-100 last:border-0"
              >
                <div>
                  <div className="font-medium text-gray-900">{c.legalName || '—'}</div>
                  {c.address && <div className="text-xs text-gray-600">{c.address}</div>}
                  {c.federalBusinessNumber && (
                    <div className="text-xs text-gray-600">
                      {t('companies.listFederal')}: {c.federalBusinessNumber}
                    </div>
                  )}
                  {prov && (
                    <div className="text-xs text-gray-600">
                      {getProvincialRegLabel(t, c.addressProvince)}: {prov}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => startEdit(c)}
                    className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded"
                    aria-label={t('companies.edit')}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(c.id)}
                    className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                    aria-label={t('companies.delete')}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <form onSubmit={handleSubmit} className="space-y-2">
        {error && <p className="text-sm text-red-600">{error}</p>}

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-0.5">{t('companies.address')}</label>
          <p className="text-xs text-gray-500 mb-1">{t('companies.addressFirstHint')}</p>
          <p className="text-xs text-gray-500 mb-1">{t('companies.addressAutocompleteHint')}</p>
          <input
            ref={companyAddressInputRef}
            type="text"
            value={address}
            onChange={handleAddressChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-0"
            autoComplete="street-address"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-0.5">{t('companies.legalName')} *</label>
          <input
            type="text"
            value={legalName}
            onChange={(e) => setLegalName(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-0"
            required
          />
        </div>

        {showRegFields && (
          <div className="space-y-3 pt-1 border-t border-gray-100">
            <p className="text-xs text-gray-600">{t('companies.regNumbersIntro')}</p>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-0.5">{t('companies.province')}</label>
              <select
                value={addressProvince}
                onChange={(e) => setAddressProvince(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white min-w-0"
              >
                <option value="">{t('companies.provincePlaceholder')}</option>
                {CA_PROVINCE_CODES.map((code) => (
                  <option key={code} value={code}>
                    {t(`companies.region.${code}`)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-0.5">{t('companies.federalReg')}</label>
              <input
                type="text"
                value={federalBusinessNumber}
                onChange={(e) => setFederalBusinessNumber(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-0"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-0.5">{provincialLabel}</label>
              <input
                type="text"
                value={provincialBusinessNumber}
                onChange={(e) => setProvincialBusinessNumber(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-0"
              />
            </div>
          </div>
        )}

        <div className="flex gap-2 pt-1">
          {editingId && (
            <button type="button" onClick={resetForm} className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg">
              {t('cancel')}
            </button>
          )}
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            <Plus className="w-4 h-4" />
            {editingId ? t('companies.save') : t('companies.add')}
          </button>
        </div>
      </form>
    </div>
  );
}
